// List then watch (ui-spec §4.1): lists load through TanStack Query, then one shared watch patches the cached list in
// place. Every query key starts with [org, project] so switching either never shows the previous one's data
// (ui-spec §4.18).
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import * as React from "react";
import type { NodusClient } from "./client";
import { collectionPath, objectPath } from "./paths";
import type { KList, KObject, Scope } from "./types";
import { applyWatchEvent, type StreamInfo, type WatchManager, type WatchTarget } from "./watch";

interface ApiContextValue {
  client: NodusClient;
  watches: WatchManager;
  /** Poll collections on transports that cannot multiplex long-lived watch streams. */
  pollInterval?: number;
}

const ApiContext = React.createContext<ApiContextValue | null>(null);

export function ApiProvider({ client, watches, pollInterval, children }: ApiContextValue & { children: React.ReactNode }) {
  const value = React.useMemo(() => ({ client, watches, pollInterval }), [client, watches, pollInterval]);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiContextValue {
  const v = React.useContext(ApiContext);
  if (!v) throw new Error("useApi needs an ApiProvider");
  return v;
}

export interface ListSelectors {
  labelSelector?: string;
  fieldSelector?: string;
}

export const queryKeys = {
  scope: (s: Scope) => [s.org, s.project ?? ""] as const,
  list: (s: Scope, kind: string, sel: ListSelectors = {}) =>
    [s.org, s.project ?? "", "list", kind, sel.labelSelector ?? "", sel.fieldSelector ?? ""] as const,
  object: (s: Scope, kind: string, name: string) => [s.org, s.project ?? "", "object", kind, name] as const,
  path: (s: Scope, path: string) => [s.org, s.project ?? "", "path", path] as const,
};

export interface ListResult<T extends KObject> extends KList<T> {
  /** Every page was loaded: counts are exact (P7); otherwise they render as a floor. */
  complete: boolean;
  /** Client time of the last successful list; watch events and bookmarks preserve this observation time. */
  listedAt: number;
}

/** Lists every page up to `max` items, following `continue` tokens (ADR-029). */
export async function listAll<T extends KObject>(
  client: NodusClient,
  kind: string,
  scope: Scope,
  sel: ListSelectors = {},
  max = 1000,
): Promise<ListResult<T>> {
  let items: T[] = [];
  let cont: string | undefined;
  let last: KList<T> | undefined;
  do {
    last = await client.get<KList<T>>(collectionPath(kind, scope.project), {
      org: scope.org,
      query: { limit: 500, continue: cont, labelSelector: sel.labelSelector, fieldSelector: sel.fieldSelector },
    });
    items = items.concat(last.items);
    cont = last.metadata.continue || undefined;
  } while (cont && items.length < max);
  return { ...last, items, metadata: { ...last.metadata, continue: cont }, complete: !cont, listedAt: Date.now() };
}

function useStreamInfo(watches: WatchManager, target: WatchTarget | undefined): StreamInfo | undefined {
  const subscribe = React.useCallback((fn: () => void) => watches.onChange(fn), [watches]);
  const get = React.useCallback(() => (target ? watches.info(target) : undefined), [watches, target]);
  return React.useSyncExternalStore(subscribe, get, get);
}

function useWatchInto<T extends KObject>(
  qc: QueryClient,
  watches: WatchManager,
  key: readonly unknown[],
  target: WatchTarget | undefined,
  enabled: boolean,
  relist: () => Promise<unknown>,
) {
  React.useEffect(() => {
    if (!enabled || !target) return;
    return watches.subscribe(target, {
      resourceVersion: () => qc.getQueryData<ListResult<T>>(key)?.metadata.resourceVersion,
      onEvent: (ev) => {
        qc.setQueryData<ListResult<T>>(key, (prev) => (prev ? { ...applyWatchEvent(prev, ev as never), complete: prev.complete, listedAt: prev.listedAt } : prev));
      },
      onGone: async () => {
        await relist();
      },
    });
    // The key is derived from target; re-subscribing on every render would restart streams.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, target, watches, qc]);
}

/**
 * A live collection: list, then watch from the list's resourceVersion. `relistEvery` (ms) also lists again on that
 * interval, for kinds whose served status moves with no change row (a Node's heartbeat, a token's use).
 */
/** Kinds the API lists but cannot watch (a watch answers 405); their lists refresh by relisting instead. */
const UNWATCHABLE: Record<string, number> = { Offering: 60_000, ReservedHost: 10_000 };

export function useKList<T extends KObject = KObject>(
  kind: string,
  scope: Scope,
  opts: ListSelectors & { watch?: boolean; enabled?: boolean; relistEvery?: number } = {},
) {
  const { client, watches, pollInterval } = useApi();
  const qc = useQueryClient();
  const enabled = opts.enabled ?? true;
  const sel = { labelSelector: opts.labelSelector, fieldSelector: opts.fieldSelector };
  const key = queryKeys.list(scope, kind, sel);
  const q = useQuery({
    queryKey: key,
    queryFn: () => listAll<T>(client, kind, scope, sel),
    enabled,
    staleTime: Infinity,
    refetchInterval: opts.relistEvery ?? UNWATCHABLE[kind] ?? pollInterval ?? false,
  });
  const target = React.useMemo<WatchTarget>(
    () => ({ path: collectionPath(kind, scope.project), org: scope.org, ...sel }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kind, scope.org, scope.project, opts.labelSelector, opts.fieldSelector],
  );
  useWatchInto<T>(qc, watches, key, target, enabled && !pollInterval && (opts.watch ?? !(kind in UNWATCHABLE)) && q.isSuccess, () => q.refetch());
  const stream = useStreamInfo(watches, target);
  const polled = React.useMemo<StreamInfo>(() => ({ state: q.error ? "reconnecting" : q.isSuccess ? "polling" : "connecting", ...(q.dataUpdatedAt ? { lastEventAt: new Date(q.dataUpdatedAt) } : {}) }), [q.error, q.isSuccess, q.dataUpdatedAt]);
  return { ...q, live: pollInterval ? polled : stream };
}

/** A live object: get, then watch its collection with `metadata.name=<name>`. */
export function useKObject<T extends KObject = KObject>(kind: string, name: string, scope: Scope, opts: { watch?: boolean } = {}) {
  const list = useKList<T>(kind, scope, { fieldSelector: `metadata.name=${name}`, watch: opts.watch ?? true });
  const object = list.data?.items.find((o) => o.metadata.name === name);
  return { ...list, object, notFound: list.isSuccess && !object };
}

/** Fetches a single object without a watch (for one-off reads such as a dry-run preview). */
export function useKGet<T extends KObject = KObject>(kind: string, name: string, scope: Scope) {
  const { client } = useApi();
  return useQuery({
    queryKey: queryKeys.object(scope, kind, name),
    queryFn: () => client.get<T>(objectPath(kind, name, scope.project), { org: scope.org }),
  });
}

/**
 * Pause live updates (WCAG 2.2.2): while paused the page shows the snapshot it had, the cache keeps updating, and
 * the indicator counts the changes that Resume will apply at once.
 */
export function usePausable<T>(value: T, paused: boolean): { shown: T; pending: number } {
  const snapshot = React.useRef(value);
  const [pending, setPending] = React.useState(0);
  const last = React.useRef(value);
  React.useEffect(() => {
    if (!paused) {
      snapshot.current = value;
      setPending(0);
    } else if (value !== last.current) {
      setPending((n) => n + 1);
    }
    last.current = value;
  }, [value, paused]);
  return { shown: paused ? snapshot.current : value, pending };
}
